import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useSidebar } from "@/components/ui/sidebar";

const SidebarPeekContext = createContext(null);

export function SidebarPeekProvider({ children }) {
  const { state, open, setOpen, isMobile } = useSidebar();
  const [isPeek, setIsPeek] = useState(false);
  const leaveTimerRef = useRef(null);
  const openedViaEdgeRef = useRef(false);

  const clearLeaveTimer = useCallback(() => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  }, []);

  const pinOpen = useCallback(() => {
    openedViaEdgeRef.current = false;
    setIsPeek(false);
    clearLeaveTimer();
  }, [clearLeaveTimer]);

  const revealFromEdge = useCallback(() => {
    if (isMobile || state !== "collapsed") return;
    clearLeaveTimer();
    openedViaEdgeRef.current = true;
    setIsPeek(true);
    setOpen(true);
  }, [clearLeaveTimer, isMobile, setOpen, state]);

  const schedulePeekClose = useCallback(() => {
    if (!isPeek) return;
    clearLeaveTimer();
    leaveTimerRef.current = setTimeout(() => {
      openedViaEdgeRef.current = false;
      setIsPeek(false);
      setOpen(false);
    }, 280);
  }, [clearLeaveTimer, isPeek, setOpen]);

  // Peek state mirrors the sidebar's `open`, which any consumer of the sidebar
  // context can change. There is no single event to hang this off, so the effect
  // is the synchronisation point.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) {
      openedViaEdgeRef.current = false;
      setIsPeek(false);
      clearLeaveTimer();
      return;
    }

    if (!openedViaEdgeRef.current) {
      setIsPeek(false);
    }
  }, [clearLeaveTimer, open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  useEffect(() => {
    if (!isPeek || !open || isMobile) return undefined;

    const sidebar = document.querySelector('[data-slot="sidebar-container"]');
    if (!sidebar) return undefined;

    const handleEnter = () => clearLeaveTimer();
    const handleLeave = () => schedulePeekClose();

    sidebar.addEventListener("mouseenter", handleEnter);
    sidebar.addEventListener("mouseleave", handleLeave);

    return () => {
      sidebar.removeEventListener("mouseenter", handleEnter);
      sidebar.removeEventListener("mouseleave", handleLeave);
    };
  }, [clearLeaveTimer, isMobile, isPeek, open, schedulePeekClose]);

  useEffect(() => {
    const wrapper = document.querySelector('[data-slot="sidebar-wrapper"]');
    if (!wrapper) return undefined;

    wrapper.classList.toggle("sidebar-peeking", isPeek);
    return () => wrapper.classList.remove("sidebar-peeking");
  }, [isPeek]);

  useEffect(() => () => clearLeaveTimer(), [clearLeaveTimer]);

  const value = { isPeek, pinOpen, revealFromEdge };

  return (
    <SidebarPeekContext.Provider value={value}>
      {children}
      {!isMobile && isPeek && (
        <div
          className="sidebar-edge-zone fixed inset-y-0 left-0 z-40 w-3 max-[767px]:w-4"
          onMouseEnter={revealFromEdge}
          aria-hidden="true"
        />
      )}
    </SidebarPeekContext.Provider>
  );
}

export function useSidebarPeek() {
  const context = useContext(SidebarPeekContext);
  if (!context) {
    throw new Error("useSidebarPeek must be used within SidebarPeekProvider.");
  }
  return context;
}
